import { Component, Injector, OnInit, HostListener } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/Services/api.service';
import { ModelEditor } from 'src/app/shared/editors/modeleditor';

@Component({
    selector: 'app-editcitationtemplate',
    templateUrl: './editcitationtemplate.component.html',
    styleUrls: ['./editcitationtemplate.component.scss'],
    standalone: false
})
export class EditCitationTemplateComponent extends ModelEditor implements OnInit {
  public showFormattingHelp: boolean = false;
  public visibleBarcodeSettings: number | null = null;
  public visibleConditionEditor: number | null = null;
  public visibleIterationEditor: number | null = null;

  public lineTypes = [
    { value: 'text', label: 'Text' },
    { value: 'barcode', label: 'Barcode' }
  ];
  public barcodeTypes = [
    { value: 'Code128', label: 'Code 128' },
    { value: 'QR', label: 'QR Code' },
    { value: 'Code39', label: 'Code 39' },
    { value: 'PDF417', label: 'PDF 417' }
  ];
  public barcodeOrientations = [
    { value: 'Horizontal', label: 'Horizontal' },
    { value: 'Vertical', label: 'Vertical' }
  ];
  public humanReadableSettings = [
    { value: 'false', label: 'None' },
    { value: 'true', label: 'Below' },
  ];
  
  @HostListener('document:click', ['$event'])
  clickOutside(event: any) {
    if (this.showFormattingHelp && !event.target.closest('.dropdown')) {
      this.showFormattingHelp = false;
    }
  }

  constructor(
    private apiService: ApiService,
    private injector: Injector,
    private fb: FormBuilder
  ) {
    super("enforcement/citations/templates", injector);
    this.router = injector.get(Router);
  }

  public override DefaultModel() {
    return {
      Name: 'New Citation Template',
      Description: '',
      FeedLinesAfterPrint: 1,
      FeedLinesBeforePrint: 1,
      Lines: []
    };
  }

  public override AfterModelLoaded(): void {
    if (!this.Model.Lines) {
      this.Model.Lines = [];
    }
    
    this.Model.Lines.sort((a: any, b: any) => a.Index - b.Index);
    this.Model.Lines.forEach((line: any) => {
      if (line.Alignment === undefined) {
        line.Alignment = 0; 
      }
      if (line.LineType === undefined) {
        line.LineType = 'text';
      }
      if (line.LineType == 'barcode') {
        if (line.BarcodeType === undefined) {
          line.BarcodeType = 'Code128';
        }
        if (line.Orientation === undefined) {
          line.Orientation = 'Horizontal';
        }
        if (line.Width === undefined) {
          line.Width = 150;
        }
        if (line.Height === undefined) {
          line.Height = 50;
        }
      }
    });
    
    this.FormLoaded = true;
  }

  public override BeforeSave(): boolean | void {
    if (!this.Model.Lines || this.Model.Lines.length === 0) {
      this.toast.warning('At least one template line is required', 'Error');
      return false;
    }
    
    this.Model.Lines.forEach((line: any, index: number) => {
      line.Index = index;
    });
    
    const linesArray = new UntypedFormArray([]);
    
    this.Model.Lines.forEach((line: any) => {
      const lineGroup = new UntypedFormGroup({});
      
      Object.keys(line).forEach(key => {
        lineGroup.addControl(key, new UntypedFormControl(line[key]));
      });
      
      linesArray.push(lineGroup);
    });
    
    if (this.Form.get('Lines')) {
      this.Form.setControl('Lines', linesArray);
    } else {
      this.Form.addControl('Lines', linesArray);
    }
    
    return true;
  }

  public override Validators() {
    return {
      'Name': [Validators.required, Validators.maxLength(100)],
      'Description': [Validators.maxLength(500)],
      'FeedLinesBeforePrint': [Validators.required, Validators.min(0), Validators.max(10)],
      'FeedLinesAfterPrint': [Validators.required, Validators.min(0), Validators.max(10)],
    };
  }

  public addTextLine() {
    const newLine = {
      Content: '',
      Index: this.Model.Lines.length,
      Alignment: 0, // Default to left alignment (0=left, 1=center, 2=right)
      LineType: 'text'
    };
    
    this.Model.Lines.push(newLine);
    this.Form.markAsDirty();
  }

  public addBarcodeLine() {
    const newLine = {
      Content: '',
      Index: this.Model.Lines.length,
      LineType: 'barcode',
      BarcodeType: 'Code128',
      Orientation: 'Horizontal',
      Width: 150,
      Height: 50
    };
    
    this.Model.Lines.push(newLine);
    this.visibleBarcodeSettings = this.Model.Lines.length - 1;
    this.Form.markAsDirty();
  }

  public removeLine(index: number) {
    if (this.Model.Lines && this.Model.Lines.length > index) {
      this.Model.Lines.splice(index, 1);
      
      this.Model.Lines.forEach((line: any, idx: number) => {
        line.Index = idx;
      });
      
      this.visibleBarcodeSettings = null;
      
      this.Form.markAsDirty();
    }
  }

  public moveLineUp(index: number) {
    if (index > 0 && this.Model.Lines && this.Model.Lines.length > index) {
      const temp = this.Model.Lines[index - 1];
      this.Model.Lines[index - 1] = this.Model.Lines[index];
      this.Model.Lines[index] = temp;
      
      this.Model.Lines.forEach((line: any, idx: number) => {
        line.Index = idx;
      });
      
      this.visibleBarcodeSettings = null;
      
      this.Form.markAsDirty();
    }
  }

  public moveLineDown(index: number) {
    if (this.Model.Lines && this.Model.Lines.length > index + 1) {
      const temp = this.Model.Lines[index + 1];
      this.Model.Lines[index + 1] = this.Model.Lines[index];
      this.Model.Lines[index] = temp;
      
      this.Model.Lines.forEach((line: any, idx: number) => {
        line.Index = idx;
      });
      
      this.visibleBarcodeSettings = null;
      
      this.Form.markAsDirty();
    }
  }

  public toggleFormattingHelp(event: MouseEvent): void {
    event.stopPropagation();
    this.showFormattingHelp = !this.showFormattingHelp;
  }

  public toggleBarcodeSettings(index: number): void {
    if (this.visibleBarcodeSettings === index) {
      this.visibleBarcodeSettings = null;
    } else {
      this.visibleBarcodeSettings = index;
    }
  }

  // Conditional builder support
  public conditionOperators = [
    { value: 'isTrue', label: 'is True', requiresValue: false },
    { value: 'isFalse', label: 'is False', requiresValue: false },
  ];

  public suggestedProperties: string[] = [
    'IsWarning'
  ];

  // Iteration (foreach) builder support
  public suggestedCollectionProperties: string[] = [
    'CitationViolations',
    'CitationMedia',
    'EscalationSchedule'
  ];

  public iterationDraft: {
    collectionPath: string;
    bodyText: string;
  } = {
      collectionPath: '',
      bodyText: ''
    };

  public conditionDraft: {
    propertyPath: string;
    operator: string;
    value: string;
    thenText: string;
    elseText: string;
    elseEnabled: boolean;
  } = {
      propertyPath: 'IsWarning',
      operator: 'isTrue',
      value: '',
      thenText: '',
      elseText: '',
      elseEnabled: false
    };

  public visibleLineStyleIndex: number | null = null;
  public visibleThenStyleIndex: number | null = null;
  public visibleElseStyleIndex: number | null = null;

  // Ensure only one "raw-tag" input is active at a time
  public focusLineInput(index: number): void {
    this.visibleLineStyleIndex = index;
    this.visibleThenStyleIndex = null;
    this.visibleElseStyleIndex = null;
  }

  public focusThenInput(index: number): void {
    this.visibleThenStyleIndex = index;
    this.visibleLineStyleIndex = null;
    this.visibleElseStyleIndex = null;
  }

  public focusElseInput(index: number): void {
    this.visibleElseStyleIndex = index;
    this.visibleLineStyleIndex = null;
    this.visibleThenStyleIndex = null;
  }

  /**
   * Parse an {if:...} condition expression into UI-friendly parts.
   * Supports: prop, !prop, prop OP value  where OP in (==, !=, >, <, >=, <=)
   */
  private parseConditionExpression(expression: string): { propertyPath: string; operator: string; value: string } {
    const raw = (expression || '').trim();
    if (!raw) {
      return { propertyPath: '', operator: 'isTrue', value: '' };
    }

    // NOT: !IsWarning
    if (raw.startsWith('!')) {
      return {
        propertyPath: raw.substring(1).trim(),
        operator: 'isFalse',
        value: ''
      };
    }

    // Binary operators
    const opMatch = raw.match(/^(.*?)(==|!=|>=|<=|>|<)(.*)$/);
    if (opMatch) {
      const prop = opMatch[1].trim();
      const op = opMatch[2];
      let value = opMatch[3].trim();

      // Strip single quotes for strings and un-escape doubled quotes
      if (value.startsWith("'") && value.endsWith("'") && value.length >= 2) {
        value = value.substring(1, value.length - 1).replace(/''/g, "'");
      }

      return {
        propertyPath: prop,
        operator: op,
        value: value
      };
    }

    // Simple truthy: IsWarning
    return {
      propertyPath: raw,
      operator: 'isTrue',
      value: ''
    };
  }

  public toggleConditionEditor(index: number) {
    const line = this.Model?.Lines?.[index];
    // Reset draft each time we open
    if (this.visibleConditionEditor !== index) {
      this.conditionDraft = { propertyPath: '', operator: 'isTrue', value: '', thenText: '', elseText: '', elseEnabled: false };

      if (line) {
        const content: string = line.Content || '';
        const ifElseRegex = /^\{if:[\s\S]*?\}([\s\S]*?)\{else\}([\s\S]*?)\{endif\}\s*$/;
        const simpleIfRegex = /^\{if:[\s\S]*?\}([\s\S]*?)\{endif\}\s*$/;

        if (ifElseRegex.test(content)) {
          const m = content.match(ifElseRegex);
          this.conditionDraft.thenText = (m && m[1]) ? m[1] : '';
          this.conditionDraft.elseText = (m && m[2]) ? m[2] : '';
          this.conditionDraft.elseEnabled = !!(m && m[2] && m[2].length > 0);
        } else if (simpleIfRegex.test(content)) {
          const m = content.match(simpleIfRegex);
          this.conditionDraft.thenText = (m && m[1]) ? m[1] : '';
          this.conditionDraft.elseText = '';
          this.conditionDraft.elseEnabled = false;
        } else {
          // Not currently conditional; default THEN text to existing line content
          this.conditionDraft.thenText = content;
          this.conditionDraft.elseText = '';
          this.conditionDraft.elseEnabled = false;
        }

        // Also hydrate the property/operator/value from the existing {if:...} expression if there is one
        const outer = this.currentOuterCondition(content);
        if (outer) {
          const parsed = this.parseConditionExpression(outer);
          this.conditionDraft.propertyPath = parsed.propertyPath;
          this.conditionDraft.operator = parsed.operator;
          this.conditionDraft.value = parsed.value;
        }
      }
    }
    this.visibleConditionEditor = this.visibleConditionEditor === index ? null : index;
  }

  public hasIteration(content: string | null | undefined): boolean {
    if (!content) return false;
    return /^\{each:[\s\S]*?\}[\s\S]*\{endeach\}\s*$/.test(content);
  }

  public extractIterationCollection(content: string | null | undefined): string {
    if (!content) return '';
    const m = content.match(/^\{each:([\s\S]*?)\}[\s\S]*\{endeach\}\s*$/);
    return m && m[1] != null ? m[1] : '';
  }

  public extractIterationBody(content: string | null | undefined): string {
    if (!content) return '';
    const m = content.match(/^\{each:[\s\S]*?\}([\s\S]*?)\{endeach\}\s*$/);
    return m && m[1] != null ? m[1] : '';
  }

  public toggleIterationEditor(index: number) {
    const line = this.Model?.Lines?.[index];
    if (this.visibleIterationEditor !== index) {
      // Initialise from existing content, preferring any current {each:...}{endeach} wrapper.
      const content: string = line?.Content || '';
      if (this.hasIteration(content)) {
        this.iterationDraft = {
          collectionPath: this.extractIterationCollection(content),
          bodyText: this.extractIterationBody(content)
        };
      } else {
        this.iterationDraft = {
          collectionPath: '',
          bodyText: content
        };
      }
    }
    this.visibleIterationEditor = this.visibleIterationEditor === index ? null : index;
  }

  public applyIterationToLine(index: number) {
    const line = this.Model?.Lines?.[index];
    if (!line) return;

    const path = (this.iterationDraft.collectionPath || '').trim();
    if (!path) return;

    const body = (this.iterationDraft.bodyText ?? '').toString();
    line.Content = `{each:${path}}${body}{endeach}`;

    this.Form.markAsDirty();
    this.visibleIterationEditor = null;
  }

  public removeIterationFromLine(index: number) {
    const line = this.Model?.Lines?.[index];
    if (!line || !line.Content) return;
    if (!this.hasIteration(line.Content)) return;

    const body = this.extractIterationBody(line.Content);
    line.Content = body;

    this.Form.markAsDirty();
    if (this.visibleIterationEditor === index) {
      this.visibleIterationEditor = null;
    }
  }

  public enableElse() {
    this.conditionDraft.elseEnabled = true;
    if (!this.conditionDraft.elseText) {
      this.conditionDraft.elseText = '';
    }
  }

  private quoteIfNeeded(val: string): string {
    if (val == null) return "''";
    const trimmed = ('' + val).trim();
    if (trimmed === '') return "''";
    // numeric?
    if (!isNaN(Number(trimmed))) return trimmed;
    // boolean?
    const lower = trimmed.toLowerCase();
    if (lower === 'true' || lower === 'false') return lower;
    // quoted string - escape single quotes by doubling
    const escaped = trimmed.replace(/'/g, "''");
    return `'${escaped}'`;
  }

  public applyConditionToLine(index: number) {
    const line = this.Model?.Lines?.[index];
    if (!line) return;
    const prop = (this.conditionDraft.propertyPath || '').trim();
    if (!prop) return;

    let expression: string;
    switch (this.conditionDraft.operator) {
      case 'isTrue':
        expression = prop;
        break;
      case 'isFalse':
        expression = '!' + prop;
        break;
      default:
        expression = `${prop}${this.conditionDraft.operator}${this.conditionDraft.value != null ? this.quoteIfNeeded(this.conditionDraft.value) : "''"}`;
        break;
    }

    // Use THEN/ELSE text from the condition builder; fall back to existing content if THEN is empty
    let thenText = (this.conditionDraft.thenText ?? '').toString();
    const elseText = this.conditionDraft.elseEnabled ? (this.conditionDraft.elseText ?? '').toString() : '';

    if (!thenText && line.Content && !this.hasOuterCondition(line.Content)) {
      thenText = line.Content;
    }

    if (elseText) {
      line.Content = `{if:${expression}}${thenText}{else}${elseText}{endif}`;
    } else {
      line.Content = `{if:${expression}}${thenText}{endif}`;
    }

    this.Form.markAsDirty();
    this.visibleConditionEditor = null;
  }

  public toggleLineStyle(index: number) {
    this.visibleLineStyleIndex = this.visibleLineStyleIndex === index ? null : index;
  }

  public toggleThenStyle(index: number) {
    this.visibleThenStyleIndex = this.visibleThenStyleIndex === index ? null : index;
  }

  public toggleElseStyle(index: number) {
    this.visibleElseStyleIndex = this.visibleElseStyleIndex === index ? null : index;
  }

  public isTagActive(text: string | null | undefined, tag: string): boolean {
    if (!text) return false;
    const open = `[${tag}]`;
    const close = `[/${tag}]`;
    return text.indexOf(open) !== -1 && text.indexOf(close) !== -1;
  }

  public removeConditionFromLine(index: number) {
    const line = this.Model?.Lines?.[index];
    if (!line || !line.Content) return;
    // Remove a single outer {if:...}{else}...{endif} or {if:...}{endif} wrapper, keeping the "true" branch
    const ifElseRegex = /^\{if:[\s\S]*?\}([\s\S]*?)\{else\}([\s\S]*?)\{endif\}\s*$/;
    const simpleIfRegex = /^\{if:[\s\S]*?\}([\s\S]*?)\{endif\}\s*$/;
    if (ifElseRegex.test(line.Content)) {
      const m = line.Content.match(ifElseRegex);
      if (m && m[1] != null) {
        line.Content = m[1];
      }
    } else if (simpleIfRegex.test(line.Content)) {
      const m = line.Content.match(simpleIfRegex);
      if (m && m[1] != null) {
        line.Content = m[1];
      }
    }
    this.Form.markAsDirty();
    if (this.visibleConditionEditor === index) this.visibleConditionEditor = null;
  }

  public hasOuterCondition(content: string): boolean {
    if (!content) return false;
    return /^\{if:[\s\S]*?\}[\s\S]*\{endif\}\s*$/.test(content);
  }

  public currentOuterCondition(content: string): string | null {
    if (!content) return null;
    const m = content.match(/^\{if:([\s\S]*?)\}/);
    return m ? m[1] : null;
  }

  public operatorRequiresValue(opValue: string): boolean {
    const op = this.conditionOperators.find(o => o.value === opValue);
    return !!(op && op.requiresValue);
  }

  public applyInlineTag(target: HTMLInputElement | HTMLTextAreaElement, tag: string) {
    if (!target) return;
    const start = target.selectionStart ?? 0;
    const end = target.selectionEnd ?? 0;
    const value = target.value ?? '';
    const selected = value.substring(start, end);
    const open = `[${tag}]`;
    const close = `[/${tag}]`;

    // If tag already present anywhere in the text, treat this as a toggle off and strip all instances
    if (this.isTagActive(value, tag)) {
      const stripped = value.split(open).join('').split(close).join('');
      target.value = stripped;
      try {
        const pos = Math.min(stripped.length, start);
        target.setSelectionRange(pos, pos);
      } catch { }
    } else {
      const newValue = value.slice(0, start) + open + selected + close + value.slice(end);
      target.value = newValue;

      const cursorStart = start + open.length;
      const cursorEnd = cursorStart + selected.length;
      try {
        target.setSelectionRange(cursorStart, cursorEnd);
      } catch { }
    }

    target.focus();
    // trigger Angular ngModel update
    target.dispatchEvent(new Event('input', { bubbles: true } as any));
  }

  public extractThenText(content: string | null | undefined): string {
    if (!content) return '';
    const ifElseRegex = /^\{if:[\s\S]*?\}([\s\S]*?)\{else\}([\s\S]*?)\{endif\}\s*$/;
    const simpleIfRegex = /^\{if:[\s\S]*?\}([\s\S]*?)\{endif\}\s*$/;

    let m = content.match(ifElseRegex);
    if (m && m[1] != null) {
      return m[1];
    }

    m = content.match(simpleIfRegex);
    if (m && m[1] != null) {
      return m[1];
    }

    return content;
  }

  public extractElseText(content: string | null | undefined): string {
    if (!content) return '';
    const ifElseRegex = /^\{if:[\s\S]*?\}([\s\S]*?)\{else\}([\s\S]*?)\{endif\}\s*$/;
    const m = content.match(ifElseRegex);
    if (m && m[2] != null) {
      return m[2];
    }
    return '';
  }

  public hasElseBranch(content: string | null | undefined): boolean {
    if (!content) return false;
    const ifElseRegex = /^\{if:[\s\S]*?\}([\s\S]*?)\{else\}([\s\S]*?)\{endif\}\s*$/;
    return ifElseRegex.test(content);
  }

  private readonly formattingTagRegex = /\[(\/?)(b|i|u|m|l|uc|st)\]/gi;

  public stripFormattingTags(text: string | null | undefined): string {
    if (!text) return '';
    return text.replace(this.formattingTagRegex, '');
  }

  /**
   * Returns CSS classes to visually reflect formatting tags
   * when the line is in preview (non-edit) mode.
   */
  public getPreviewClasses(content: string | null | undefined): { [klass: string]: boolean } {
    const value = content ?? '';
    return {
      'preview-bold': this.isTagActive(value, 'b'),
      'preview-italic': this.isTagActive(value, 'i'),
      'preview-underline': this.isTagActive(value, 'u'),
      'preview-medium': this.isTagActive(value, 'm'),
      'preview-large': this.isTagActive(value, 'l'),
      'preview-uc': this.isTagActive(value, 'uc'),
      'preview-strike': this.isTagActive(value, 'st')
    };
  }
} 
